import { Typography } from '@mui/material'
import { CircleMinusMajor, CirclePlusMajor } from '@shopify/polaris-icons'
import { useFormikContext } from 'formik'
import { Fragment, useCallback, useMemo, useState } from 'react'
import {
  DocusignFieldTabLabel,
  DocusignSectionTitle,
  DocusignVehicleType,
  TaxClassificationType,
  docusignSectionsMap,
  docusignSectionsTitleMap,
} from 'src/constants/docusign'
import { DocusignSigningField } from 'src/interfaces/docusign'
import tw from 'twin.macro'
import { Button } from '../shared/Button'
import { DocusignFieldsFormModalValues } from './DocusignFieldsFormModal.interface'
import { DocusignFieldsFormModalInput } from './DocusignFieldsFormModalInput'
import { InfoContainer } from '../shared/InfoContainer'
import React from 'react'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'

interface Props {
  fields: Array<DocusignSigningField>
}

type TINType = 'ssn' | 'ein'

interface TINOption {
  value: TINType
  label: string
}

const tinOptions: TINOption[] = [
  {
    value: 'ssn',
    label: 'Enter Social Security Number (SSN)',
  },
  {
    value: 'ein',
    label: 'Enter Employer Identification Number (EIN)',
  },
]

export const DocusignFieldsFormModalInputList = ({ fields }: Props) => {
  const { touched, errors, values, setFieldValue } =
    useFormikContext<DocusignFieldsFormModalValues>()

  const [showAdditionalVehicleInformation1, setShowAdditionalVehicleInformation1] =
    useState<boolean>(false)
  const [showAdditionalVehicleInformation2, setShowAdditionalVehicleInformation2] =
    useState<boolean>(false)
  const [tinType, setTinType] = useState<TINType | undefined>(undefined)

  const orderedFields = useMemo<
    { sectionKey: DocusignSectionTitle; fields: DocusignSigningField[] }[]
  >(() => {
    const orderedFieldsRes: {
      sectionKey: DocusignSectionTitle
      fields: DocusignSigningField[]
    }[] = []

    Object.entries(docusignSectionsMap).forEach(([key, fieldItems]) => {
      const res: { sectionKey: DocusignSectionTitle; fields: DocusignSigningField[] } = {
        sectionKey: key as DocusignSectionTitle,
        fields: [],
      }

      const taxClassificationTypeValue = values[DocusignFieldTabLabel.taxClassificationType]

      const vehicleTypeValue = values[DocusignFieldTabLabel.vehicleType]

      fieldItems.forEach((fieldItem) => {
        const fieldToAdd = fields.find((field) => field.tabLabel === fieldItem)

        if (
          fieldItem === DocusignFieldTabLabel.llcType &&
          taxClassificationTypeValue !== TaxClassificationType.Llc
        ) {
          return
        }

        if (
          fieldItem === DocusignFieldTabLabel.otherTaxClassification &&
          taxClassificationTypeValue !== TaxClassificationType.Other
        ) {
          return
        }

        if (
          fieldItem === DocusignFieldTabLabel.vehicleTypeOther &&
          vehicleTypeValue !== DocusignVehicleType.Other
        ) {
          return
        }

        if (fieldToAdd) {
          res.fields.push(fieldToAdd)
        }
      })

      if (res.fields.length) {
        orderedFieldsRes.push(res)
      }
    })

    return orderedFieldsRes
  }, [fields, values])

  const hasVehicleInformation1 = useMemo(
    () => fields.some((item) => item.tabLabel === DocusignFieldTabLabel.vehicleMake2),
    [fields],
  )
  const hasVehicleInformation2 = useMemo(
    () => fields.some((item) => item.tabLabel === DocusignFieldTabLabel.vehicleMake3),
    [fields],
  )

  const addVehicleInformation1 = useCallback(() => {
    setShowAdditionalVehicleInformation1(true)
  }, [])

  const addVehicleInformation2 = useCallback(() => {
    setShowAdditionalVehicleInformation2(true)
  }, [])

  const removeVehicleInformation1 = useCallback(() => {
    setShowAdditionalVehicleInformation1(false)

    setFieldValue(DocusignFieldTabLabel.vehicleMake2, '')
    setFieldValue(DocusignFieldTabLabel.vehicleModel2, '')
    setFieldValue(DocusignFieldTabLabel.vehicleYear2, '')
    setFieldValue(DocusignFieldTabLabel.vehicleVIN2, '')
    setFieldValue(DocusignFieldTabLabel.vehicleLicensePlate2, '')
  }, [setFieldValue])

  const removeVehicleInformation2 = useCallback(() => {
    setShowAdditionalVehicleInformation2(false)

    setFieldValue(DocusignFieldTabLabel.vehicleMake3, '')
    setFieldValue(DocusignFieldTabLabel.vehicleModel3, '')
    setFieldValue(DocusignFieldTabLabel.vehicleYear3, '')
    setFieldValue(DocusignFieldTabLabel.vehicleVIN3, '')
    setFieldValue(DocusignFieldTabLabel.vehicleLicensePlate3, '')
  }, [setFieldValue])

  const handleSelectTINType = useCallback((_: any, value: TINType) => {
    setTinType(value)

    if (value === 'ssn') {
      setFieldValue(DocusignFieldTabLabel.w9Ein, '')
    } else if (value === 'ein') {
      setFieldValue(DocusignFieldTabLabel.w9Ssn, '')
    }
  }, [])

  return (
    <div>
      {orderedFields.map((item) => {
        const isVehicleInformation =
          item.sectionKey === DocusignSectionTitle.primaryVehicleInformation
        const isVehicleInformation1 =
          item.sectionKey === DocusignSectionTitle.additionalVehicleInformation1
        const isVehicleInformation2 =
          item.sectionKey === DocusignSectionTitle.additionalVehicleInformation2

        return (isVehicleInformation1 && !showAdditionalVehicleInformation1) ||
          (isVehicleInformation2 && !showAdditionalVehicleInformation2) ? null : (
          <div css={tw`mb-6`} key={item.sectionKey}>
            <Typography variant="overline" css={tw`text-shades-400`}>
              {docusignSectionsTitleMap[item.sectionKey]}
            </Typography>
            <div css={tw`bg-shades-0 px-4 pt-2 mt-2 rounded-2xl`}>
              {item.fields.map((field) => {
                const showField =
                  (field.tabLabel !== DocusignFieldTabLabel.w9Ssn || tinType === 'ssn') &&
                  (field.tabLabel !== DocusignFieldTabLabel.w9Ein || tinType === 'ein')

                return (
                  <Fragment>
                    {field.tabLabel === DocusignFieldTabLabel.w9Ssn && (
                      <Fragment>
                        <Typography variant="h5" css={tw`mt-4 text-shades-400`}>
                          Taxpayer Identification Number (TIN)
                        </Typography>
                        <InfoContainer className={tw`mt-2 mb-4`} color="orange">
                          The number must match the legal name provided above. Use your SSN if you
                          are filing as an individual or sole proprietor. Use your EIN if you are
                          filing as a business entity (LLC, corporation, partnership, etc.). If you
                          do not have a TIN, you must obtain one before continuing.
                        </InfoContainer>
                        <Typography variant="h6" css={tw`mt-4 text-shades-400`}>
                          Select entity type
                        </Typography>
                        <div css={tw`pb-4`}>
                          <BasicRadioGroup
                            value={tinType}
                            options={tinOptions}
                            error={!tinType && touched[DocusignFieldTabLabel.w9Ssn] ? true : false}
                            helperText={errors[DocusignFieldTabLabel.w9Ssn] as string}
                            onChange={handleSelectTINType}
                            dataTestId="contractors-onboarding-docusign-fields-tin-type-input"
                          />
                        </div>
                      </Fragment>
                    )}
                    {showField && (
                      <DocusignFieldsFormModalInput key={field.tabLabel} field={field} />
                    )}
                  </Fragment>
                )
              })}
              {isVehicleInformation &&
                hasVehicleInformation1 &&
                !showAdditionalVehicleInformation1 && (
                  <Button
                    leftIcon={<CirclePlusMajor width={20} css={tw`fill-primary-500`} />}
                    color="light"
                    className={tw`w-full mb-4`}
                    onClick={addVehicleInformation1}
                    data-testid="contractors-onboarding-docusign-fields-add-vehicle-2-btn"
                  >
                    Add additional vehicle
                  </Button>
                )}
              {isVehicleInformation1 &&
                hasVehicleInformation2 &&
                !showAdditionalVehicleInformation2 && (
                  <Button
                    leftIcon={<CirclePlusMajor width={20} css={tw`fill-primary-500`} />}
                    color="light"
                    className={tw`w-full mb-4`}
                    onClick={addVehicleInformation2}
                    data-testid="contractors-onboarding-docusign-fields-add-vehicle-3-btn"
                  >
                    Add additional vehicle
                  </Button>
                )}
              {isVehicleInformation1 &&
                hasVehicleInformation2 &&
                !showAdditionalVehicleInformation2 && (
                  <Button
                    leftIcon={<CircleMinusMajor width={20} />}
                    color="grey"
                    className={tw`w-full mb-4`}
                    onClick={removeVehicleInformation1}
                    data-testid="contractors-onboarding-docusign-fields-remove-vehicle-2-btn"
                  >
                    Remove this vehicle
                  </Button>
                )}
              {isVehicleInformation2 && (
                <Button
                  leftIcon={<CircleMinusMajor width={20} />}
                  color="grey"
                  className={tw`w-full mb-4`}
                  onClick={removeVehicleInformation2}
                  data-testid="contractors-onboarding-docusign-fields-remove-vehicle-3-btn"
                >
                  Remove this vehicle
                </Button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
